// Autocomplete.tsx
import React from "react";
import {
  createElement,
  Fragment,
  useEffect,
  useRef
} from "react";
import { createRoot, Root } from "react-dom/client";
import { autocomplete, AutocompleteOptions } from "@algolia/autocomplete-js";
import "@algolia/autocomplete-theme-classic";
import styles from "./Autocomplete.module.scss";
import strings from "assets/strings";
import { HighlightText } from "components/highlightText/HighlightText";

export interface ProductItem {
  objectID: string;
  serviceName: string;
  [key: string]: any;
}

type AutocompleteProps = Partial<AutocompleteOptions<ProductItem>> & {
  className?: string;
  style?: React.CSSProperties;
  searchClient: any;
  indexName: string;
  fieldsIdList?: string;
  restrictedLetters?: RegExp;
  placeholder?: string;
  onHitsChange?: (hits: ProductItem[]) => void;
  onItemSelect?: (item: ProductItem) => void;
};

export function Autocomplete({
  className,
  searchClient,
  indexName,
  fieldsIdList,
  restrictedLetters = /[^A-Za-z0-9א-ת()\s\-]/g,
  placeholder,
  ...autocompleteProps
}: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const autocompleteInstanceRef = useRef<any>(null);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    if (!autocompleteContainer.current) return;

    autocompleteContainer.current.innerHTML = `
      <div id="autocomplete-wrapper" style="display: flex; align-items: center; gap: 8px;">    
        <label for="searchbox" class=${styles.searchLabel}>${strings.entitlementFieldLobby.fieldLabel}</label>
        <div id="autocomplete-input-container"></div>
      </div>
    `;

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: document.getElementById("autocomplete-input-container")!,
      placeholder: placeholder,
      translations: {
        detachedCancelButtonText: 'ביטול'
      },
      initialState: { query: "" },
      classNames: {
        input: styles.customInput,
        form: styles.customForm,
        submitButton: styles.customSubmitButton,
        clearButton: styles.customClearButton,
      },
      onReset() { },
      onSubmit({ state }) {
        autocompleteProps.onItemSelect?.({ objectID: '', serviceName: state.query });
      },
      onStateChange({ state, setQuery, setIsOpen }) {
        if (restrictedLetters) {
          const cleanedQuery = state.query.replace(restrictedLetters, "");
          if (state.query !== cleanedQuery) {
            setQuery(cleanedQuery);
            const inputEl = autocompleteContainer.current?.querySelector("input");
            if (inputEl) inputEl.value = cleanedQuery;
          }
        }
        if (state.query === "" && state.isOpen) {
          setIsOpen(false);
        }
      },
      shouldPanelOpen() {
        return true;
      },
      getSources({ query }) {
        const cleanedQuery = query.replace(restrictedLetters, "");
        if (query.length < 3 || query !== cleanedQuery) return [];

        return [
          {
            sourceId: "customSource",
            getItems() {
              return searchClient.search([
                {
                  indexName,
                  params: {
                    query,
                    filters: `fieldsIdList:${fieldsIdList}`,
                    restrictSearchableAttributes: ['serviceName']
                  }
                }
              ]).then(({ results }: { results: any[] }) => {
                const hits = results[0].hits;
                autocompleteProps.onHitsChange?.(hits);
                return hits;
              });
            },
            templates: {
              item({ item, state }) {
                return <HighlightText text={item.serviceName} query={state.query} />;
              }
            },
            onSelect({ item }) {
              autocompleteProps.onItemSelect?.(item);
            }
          }
        ];
      },
      renderer: { createElement, Fragment, render: () => { } },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(children);
      }
    });

    autocompleteInstanceRef.current = autocompleteInstance;

    return () => {
      autocompleteInstance.destroy();
    };
  }, []);

  useEffect(() => {
    const escListener = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isMobile) {
        setTimeout(() => {
          const inputEl = document.querySelector("input[class*=aa-Input]") as HTMLInputElement | null;

          if (inputEl && inputEl.value.trim() === "") {
            const cancelBtn = document.querySelector(".aa-DetachedCancelButton") as HTMLButtonElement | null;
            if (cancelBtn) {
              cancelBtn.click();
            } else {
              inputEl.blur(); // fallback
            }
          }
        }, 0);
      }
    };

    window.addEventListener("keydown", escListener);
    return () => {
      window.removeEventListener("keydown", escListener);
    };
  }, [isMobile]);


  return <div className={className} ref={autocompleteContainer} />;
}